import React, { useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { DockerHubProvider } from './hooks/useDockerHub';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import InferencePage from './components/InferencePage';
import ImagePage from './components/ImagePage';
import PaymentPage from './components/PaymentPage';
import SettingsPage from './components/SettingsPage';
import HelpPage from './components/HelpPage';
import PlaygroundPage from './components/PlaygroundPage';
import PlaygroundConfigPage from './components/PlaygroundConfigPage';
import OrganizationsPage from './components/OrganizationsPage';
import StoragePage from './components/StoragePage';
import AdminBillingPage from './components/AdminBillingPage';
import AdminUsersPage from './components/AdminUsersPage';
import AdminAcceleratorsPage from './components/AdminAcceleratorsPage';
import AdminProvidersPage from './components/AdminProvidersPage';
import AdminLogsPage from './components/AdminLogsPage';
import AdminGPUConfigPage from './components/AdminGPUConfigPage';
import AdminSshPoolsPage from './components/AdminSshPoolsPage';

const RestrictedPanel = ({ title, message }) => (
  <div className="max-w-md mx-auto text-center py-20">
    <ShieldAlert size={40} className="mx-auto text-gray-400 mb-4" />
    <h2 className="text-lg font-semibold text-gray-900 mb-1">{title}</h2>
    <p className="text-sm text-gray-500">{message}</p>
  </div>
);

// Renders admin-only pages only when the current user is an admin. Guards
// against a non-admin reaching an admin route (e.g. via stale/forced page
// state); the backend also enforces this and returns 403.
const AdminOnly = ({ children }) => {
  const { isAdmin } = useAuth();
  if (!isAdmin) {
    return (
      <RestrictedPanel
        title="Admins only"
        message="This page is restricted to administrators. If you believe you should have access, contact your account administrator."
      />
    );
  }
  return children;
};

// Pages an inference-only client may open. Mirrors both the sidebar nav and the
// allowlist in workers/src/auth/roles.js — the backend is what actually enforces
// it, this only keeps the UI from rendering dead controls.
const INFERENCE_ALLOWED_PAGES = ['playground', 'payment', 'settings', 'help'];

const ComputePlatform = () => (
  <AuthProvider>
    <DockerHubProvider>
      <ProtectedRoute>
        <PlatformShell />
      </ProtectedRoute>
    </DockerHubProvider>
  </AuthProvider>
);

// Lives inside AuthProvider so the landing page and the reachable page set can
// depend on the signed-in user's role.
const PlatformShell = () => {
  const { isInferenceOnly } = useAuth();
  const [currentPage, setCurrentPage] = useState(null);

  // Inference-only clients have no Dashboard, so they land on the Playground.
  const page = currentPage ?? (isInferenceOnly ? 'playground' : 'dashboard');

  const renderPage = () => {
    if (isInferenceOnly && !INFERENCE_ALLOWED_PAGES.includes(page)) {
      return (
        <RestrictedPanel
          title="Not available on this account"
          message="This account is limited to inference access. It can call and test endpoints, but cannot provision or manage compute."
        />
      );
    }

    switch (page) {
      case 'dashboard':
        return <Dashboard />;
      case 'inference':
        return <InferencePage />;
      case 'playground':
        return <PlaygroundPage />;
      case 'storage':
        return <StoragePage />;
      case 'image':
        return <ImagePage />;
      case 'payment':
        return <PaymentPage />;
      case 'settings':
        return <SettingsPage />;
      case 'help':
        return <HelpPage />;
      case 'admin-billing':
        return <AdminOnly><AdminBillingPage /></AdminOnly>;
      case 'admin-users':
        return <AdminOnly><AdminUsersPage /></AdminOnly>;
      case 'admin-accelerators':
        return <AdminOnly><AdminAcceleratorsPage /></AdminOnly>;
      case 'admin-providers':
        return <AdminOnly><AdminProvidersPage /></AdminOnly>;
      case 'admin-logs':
        return <AdminOnly><AdminLogsPage /></AdminOnly>;
      case 'admin-gpu-config':
        return <AdminOnly><AdminGPUConfigPage /></AdminOnly>;
      case 'admin-ssh-pools':
        return <AdminOnly><AdminSshPoolsPage /></AdminOnly>;
      case 'organizations':
        return <OrganizationsPage />;
      case 'admin-playground-config':
        return <AdminOnly><PlaygroundConfigPage /></AdminOnly>;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar currentPage={page} setCurrentPage={setCurrentPage} />
      <Header />
      <main className="ml-64 p-6">
        {renderPage()}
      </main>
    </div>
  );
};

export default ComputePlatform;
